<script>
	import { onMount, onDestroy } from 'svelte';
	// import { tweened } from 'svelte/motion';
	// import { cubicOut } from 'svelte/easing';
	import GridView from './GridView.svelte';
	import ModuleView from './ModuleView.svelte';
	import { mergeGridItems } from './lib/gridItems.js';
	import { gridToPixels, getImageAspectRatio } from './lib/iso.js';
	let debug = (typeof window !== 'undefined' && window.DEBUG) ? window.DEBUG : false;
	// Set server-side (isogrid/templates/index.html) from request.user.is_staff —
	// gates whether edit mode can be entered at all, independent of `?edit=1`.
	const allowEditMode = (typeof window !== 'undefined' && window.AllowEditMode) ? window.AllowEditMode : false;

	console.log('App.svelte script loaded');

	const GRID_URL = '/api/grid_items';

	// Shared data for the footer bar (desktop) and the social modal (mobile) —
	// same links rendered two ways, see .social-bar / .social-toggle CSS below.
	const SOCIAL_LINKS = [
		{ href: 'https://www.youtube.com/@Pour1nfo', icon: 'social-youtube.svg', label: 'YouTube', ariaLabel: 'YouTube', imgAlt: 'YouTube' },
		{ href: 'https://twitter.com/Romain_PourInfo', icon: 'social-twitter.svg', label: 'Twitter', ariaLabel: 'X / Twitter', imgAlt: 'Twitter / X' },
		{ href: 'https://fr.linkedin.com/in/romain-du-marais', icon: 'social-linkedin.svg', label: 'LinkedIn', ariaLabel: 'LinkedIn', imgAlt: 'LinkedIn' },
		{ href: 'https://framapiaf.org/@romainDM/', icon: 'social-mastodon.svg', label: 'Mastodon', ariaLabel: 'Mastodon', imgAlt: 'Mastodon' },
		{ href: 'https://peertube.b38.rural-it.org/c/pour_1nfo/videos', icon: 'social-peertube.svg', label: 'PeerTube', ariaLabel: 'PeerTube', imgAlt: 'PeerTube' },
		{ href: 'https://www.instagram.com/pour1nfo', icon: 'social-instagram.svg', label: 'Instagram', ariaLabel: 'Instagram', imgAlt: 'Instagram' },
		{ href: 'https://www.facebook.com/Pour1nfo', icon: 'social-facebook.svg', label: 'Facebook', ariaLabel: 'Facebook', imgAlt: 'Facebook' },
		{ href: 'https://discord.gg/9Z7qwEXEkU', icon: 'social-discord.svg', label: 'Discord (inactif)', ariaLabel: 'Discord', imgAlt: 'Discord' },
	];

	let modules = []; // AnimationModule from API (root)
	let details = []; // ContentDetail from API (root)
	let decors = [];  // Decor from API (root)
	let loading = true; // loading state
	let error = null; // error state
	let socialOpen = false;
	// Lifted here (rather than owned by GridView) so pan/zoom survive drilling
	// into/out of modules — GridView is a single, never-remounted instance.
	// Initial values are read from the URL (x/y/z) so a refresh keeps the same view.
	function currentParams() {
		return new URLSearchParams(location.search);
	}
	function numberParam(params, key, fallback) {
		if (!params.has(key)) return fallback;
		const n = Number(params.get(key));
		return Number.isFinite(n) ? n : fallback;
	}
	const initialParams = currentParams();
	let cell = numberParam(initialParams, 'z', 60);
	let panX = numberParam(initialParams, 'x', 0);
	let panY = numberParam(initialParams, 'y', 0);
	// Creative mode — `allowEditMode` (server-rendered, staff-only) gates whether
	// it can be entered at all; `?edit=1` is just the persisted on/off state for
	// someone who's allowed. Real write access is enforced server-side too, so
	// this is UI convenience, not the security boundary.
	let editMode = allowEditMode && initialParams.get('edit') === '1';
	let selectedKey = null; // item elevated above overlapping siblings, set from the corner picker
	let editError = null;

	function pushEditModeToLocation() {
		const params = currentParams();
		if (editMode) params.set('edit', '1'); else params.delete('edit');
		const qs = params.toString();
		history.replaceState(history.state, '', qs ? `?${qs}` : location.pathname);
	}
	function enableEditMode() {
		if (!allowEditMode) return;
		editMode = true;
		selectedKey = null;
		editError = null;
		pushEditModeToLocation();
	}
	function disableEditMode() {
		editMode = false;
		selectedKey = null;
		editError = null;
		pushEditModeToLocation();
	}

	let rootItems = []; // merged root modules/details/decors
	let childrenCache = {}; // code -> merged children items[], fetched once per code
	let moduleInfoCache = {}; // code -> { name, description }
	let path = []; // ordered codes of currently-open modules
	let items = []; // rendered list: root items + open modules' children, entered modules hidden
	let selectedDetail = null; // content-detail selected while a module is open (shown in panel)
	// Module view only: siblings of the selected detail (same parent module) get dimmed.
	$: dimParent = path.length > 0 && selectedDetail ? selectedDetail.parent : undefined;

	// For each depth of `path`, the world-space position of the module marker
	// entered at that depth — used as the offset for that module's children,
	// since their X/Y are authored relative to their parent module.
	function computeOffsets(codes) {
		const offsets = [];
		let base = { X: 0, Y: 0 };
		for (let i = 0; i < codes.length; i++) {
			const pool = i === 0 ? rootItems : (childrenCache[codes[i - 1]] ?? []);
			const marker = pool.find(it => it.key === codes[i]);
			base = {
				X: (marker ? Number(marker.X) : 0) + base.X,
				Y: (marker ? Number(marker.Y) : 0) + base.Y,
			};
			offsets.push(base);
		}
		return offsets;
	}

	function rebuildItems() {
		const open = new Set(path);
		const offsets = computeOffsets(path);
		items = [
			...rootItems.filter(i => !open.has(i.key)).map(i => ({ ...i, parent: null })),
			...path.flatMap((code, idx) => {
				const { X: offX, Y: offY } = offsets[idx];
				return (childrenCache[code] ?? [])
					.filter(i => !open.has(i.key))
					.map(i => ({ ...i, parent: code, X: Number(i.X) + offX, Y: Number(i.Y) + offY }));
			}),
		];
	}

	// Creative mode: persisting drag/resize edits.
	//
	// `coord_X`/`coord_Y` for a module's children are authored parent-relative
	// (see computeOffsets above), so a dragged item's on-screen (world-space)
	// X/Y must be converted back to that item's own local space before it's
	// stored/sent — using the same per-depth offset rebuildItems() already
	// applies in the other direction.
	function locateItem(key) {
		const idxRoot = rootItems.findIndex(i => i.key === key);
		if (idxRoot !== -1) return { code: null, idx: idxRoot, offset: null };
		const offsets = computeOffsets(path);
		for (let i = 0; i < path.length; i++) {
			const arr = childrenCache[path[i]] ?? [];
			const idx = arr.findIndex(i2 => i2.key === key);
			if (idx !== -1) return { code: path[i], idx, offset: offsets[i] };
		}
		return null;
	}

	function applyPatch(loc, patch) {
		if (loc.code) {
			const arr = childrenCache[loc.code];
			arr[loc.idx] = { ...arr[loc.idx], ...patch };
			childrenCache = { ...childrenCache };
		} else {
			rootItems[loc.idx] = { ...rootItems[loc.idx], ...patch };
			rootItems = [...rootItems];
		}
		rebuildItems();
	}

	function getCsrfToken() {
		const match = document.cookie.match(/csrftoken=([^;]+)/);
		return match ? decodeURIComponent(match[1]) : '';
	}

	async function patchItemOnServer(item, prevSnapshot, loc) {
		try {
			const res = await fetch(`/api/grid_items/${item.type}/${item.id}/`, {
				method: 'PATCH',
				credentials: 'same-origin',
				headers: {
					'Content-Type': 'application/json',
					'X-CSRFToken': getCsrfToken(),
				},
				body: JSON.stringify({ X: item.X, Y: item.Y, W: item.W, L: item.L }),
			});
			if (!res.ok) throw new Error(`HTTP ${res.status}`);
			const saved = await res.json();
			applyPatch(loc, saved);
			editError = null;
		} catch (err) {
			applyPatch(loc, prevSnapshot);
			editError = (err && err.message) ? err.message : 'Échec de la sauvegarde';
		}
	}

	// `worldPatch` is in the same world-space as rendered `items` (what the
	// drag/resize handlers in IsoMarker computed); it's converted to the
	// item's own local space before being written to rootItems/childrenCache
	// and sent to the server, since that's what's actually stored there.
	function updateItemCoords(key, worldPatch) {
		const loc = locateItem(key);
		if (!loc) return;
		const arr = loc.code ? childrenCache[loc.code] : rootItems;
		const prevSnapshot = { ...arr[loc.idx] };
		const localPatch = loc.offset
			? { X: worldPatch.X - loc.offset.X, Y: worldPatch.Y - loc.offset.Y, W: worldPatch.W, L: worldPatch.L }
			: { X: worldPatch.X, Y: worldPatch.Y, W: worldPatch.W, L: worldPatch.L };
		applyPatch(loc, localPatch);
		const freshItem = loc.code ? childrenCache[loc.code][loc.idx] : rootItems[loc.idx];
		patchItemOnServer(freshItem, prevSnapshot, loc);
	}

	async function selectItem(it) {
		selectedKey = it.key;
		await centerOn(it);
	}

	async function ensureModuleCached(code) {
		if (childrenCache[code]) return;
		const res = await fetch(`/api/grid_items/${code}/`);
		if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
		const data = await res.json();
		childrenCache[code] = mergeGridItems(data.modules || [], data.details || [], data.decors || []);
		moduleInfoCache[code] = data.module || { name: '', description: '' };
	}

	function pushPathToLocation() {
		const params = currentParams();
		if (path.length) params.set('m', path.join(',')); else params.delete('m');
		const qs = params.toString();
		history.pushState({}, '', qs ? `?${qs}` : location.pathname);
	}

	// Debounced so continuous drag/zoom doesn't spam history.replaceState.
	let panSaveTimeout = null;
	function savePanToLocation() {
		if (panSaveTimeout) clearTimeout(panSaveTimeout);
		panSaveTimeout = setTimeout(() => {
			const params = currentParams();
			params.set('x', Math.round(panX));
			params.set('y', Math.round(panY));
			params.set('z', Math.round(cell));
			const qs = params.toString();
			history.replaceState(history.state, '', qs ? `?${qs}` : location.pathname);
		}, 400);
	}
	$: savePanToLocation(panX, panY, cell);

	// ModuleView's fixed width (see ModuleView.svelte) — the grid stays full-bleed
	// behind it, so centering must bias into the visible left portion (panel is on the right).
	const PANEL_W = 320;
	// On small screens ModuleView docks to the bottom instead (max-height: 45dvh),
	// so centering biases into the visible top portion. Keep in sync with its media query.
	const MOBILE_MQ = '(max-width: 768px)';
	const PANEL_H_RATIO = 0.45;

	let panAnimRaf = null;

	function cubicOut(t) {
		const f = t - 1;
		return f * f * f + 1;
	}

	// Tweens panX/panY to a target over `duration` ms — panX/panY stay plain
	// numbers (GridView binds them directly for drag/zoom), so this animates
	// by hand instead of using a svelte/motion store.
	function animatePanTo(targetX, targetY, duration = 250) {
		if (panAnimRaf) cancelAnimationFrame(panAnimRaf);
		const startX = panX;
		const startY = panY;
		const startTime = performance.now();
		function step(now) {
			const t = Math.min(1, (now - startTime) / duration);
			const eased = cubicOut(t);
			panX = startX + (targetX - startX) * eased;
			panY = startY + (targetY - startY) * eased;
			panAnimRaf = t < 1 ? requestAnimationFrame(step) : null;
		}
		panAnimRaf = requestAnimationFrame(step);
	}

	async function centerOn(item) {
		// gridToPixels needs an aspect ratio to size height — load it the same way
		// IsoMarker does before computing pos.
		let aspectRatio = null;
		if (item.img) {
			try {
				aspectRatio = await getImageAspectRatio(item.img);
			} catch {
				aspectRatio = null;
			}
		}
		const pos = gridToPixels(item.X, item.Y, item.W, item.L, cell, aspectRatio);
		const mobile = window.matchMedia(MOBILE_MQ).matches;
		const centerX = mobile ? window.innerWidth / 2 : (window.innerWidth - PANEL_W) / 2;
		const centerY = mobile ? window.innerHeight * (1 - PANEL_H_RATIO) / 2 : window.innerHeight / 2;
		const targetX = centerX - pos.x;
		const targetY = centerY - (pos.y + (Number.isFinite(pos.h) ? pos.h / 2 : 0));
		animatePanTo(targetX, targetY);
	}

	async function openModule(e) {
		const code = e.detail.code;
		const clickedItem = items.find(i => i.key === code);
		try {
			await ensureModuleCached(code);
		} catch (err) {
			error = err.message;
			return;
		}
		path = [...path, code];
		selectedDetail = null;
		rebuildItems();
		pushPathToLocation();
		if (clickedItem) await centerOn(clickedItem);
	}
	function closeToDepth(depth) {
		path = path.slice(0, depth);
		selectedDetail = null;
		rebuildItems();
		pushPathToLocation();
	}
	function closeModule() {
		closeToDepth(path.length - 1);
	}
	async function syncPathFromLocation() {
		const raw = new URLSearchParams(location.search).get('m');
		const codes = raw ? raw.split(',').filter(Boolean) : [];
		for (const code of codes) {
			try {
				await ensureModuleCached(code);
			} catch (err) {
				error = err.message;
				break;
			}
		}
		path = codes;
		rebuildItems();
	}
	function handlePopState() {
		syncPathFromLocation();
		const params = currentParams();
		panX = numberParam(params, 'x', panX);
		panY = numberParam(params, 'y', panY);
		cell = numberParam(params, 'z', cell);
	}

	function handleDetailOpen(e) {
		const key = e?.detail?.id;
		const it = items.find(x => x.key === key);
		if (!it) return;
		selectedDetail = it;
		// Hash tracks detail selection only at root — inside a module, selection
		// state lives in `path`/`selectedDetail` already reflected by ?m=.
		// Written via pushState rather than `location.hash =` — the latter is a
		// real fragment navigation, and the browser responds by scrolling the
		// matching-id marker (IsoMarker sets `id={key}`) into view. That marker
		// sits inside `.root-grid`'s `position: fixed` ancestor chain, so it
		// can't actually move on screen, but the forced scroll attempt still
		// corrupts the fixed grid's background repaint at the bottom of the
		// viewport. pushState changes the URL (fragment included) without
		// triggering that scroll-to-anchor behavior.
		if (path.length === 0) {
			history.pushState(history.state, '', `${location.pathname}${location.search}#${encodeURIComponent(it.key)}`);
		}
	}

	function openById(key) {
		const it = items.find(x => x.key === key);
		if (it) selectedDetail = it;
	}

	function handleHashChange() {
		const hash = decodeURIComponent(window.location.hash.replace(/^#/, ''));
		if (hash) {
			openById(hash);
		} else if (path.length === 0) {
			selectedDetail = null;
		}
	}

	// Closes the panel: goes up a module level if one is open (existing
	// behavior), otherwise just clears the root-level detail selection.
	function closePanel() {
		if (path.length > 0) {
			closeModule();
			return;
		}
		selectedDetail = null;
		history.replaceState(null, '', location.pathname + location.search);
	}

	async function fetchItems() {
		loading = true;
		error = null;
		try {
			if(debug) console.log('[grid] Fetching from:', GRID_URL);
			const response = await fetch(GRID_URL);
			if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
			const data = await response.json();
			modules = data.modules || [];
			details = data.details || [];
			decors  = data.decors  || [];
			rootItems = mergeGridItems(modules, details, decors);
			rebuildItems();
			handleHashChange();
		} catch (err) {
			console.error('[grid] Error fetching data:', err);
			modules = [];
			details = [];
			decors  = [];
			rootItems = [];
			rebuildItems();
			error = err.message;
		} finally {
			loading = false;
		}
	}

	// // Animated spaceship coordinates using tweened
	// const spaceshipPos = tweened({ X: 10.5, Y: 8 }, {
	// 	duration: 2000,
	// 	easing: cubicOut
	// });

	// // Define waypoints for the spaceship to follow
	// const waypoints = [
	// 	{ X: 10.5, Y: 8 },
	// 	{ X: 10.5, Y: -7 },
	// 	{ X: 8.5, Y: -7 },
	// 	{ X: 8.5, Y: 8 },
	// 	{ X: 10.5, Y: 8 },
	// ];

	// let currentWaypoint = 0;
	// let animationInterval;

	// function animateSpaceship() {
	// 	// Move to next waypoint
	// 	currentWaypoint = (currentWaypoint + 1) % waypoints.length;
	// 	const target = waypoints[currentWaypoint];
		
	// 	// Update the tweened store with new target
	// 	spaceshipPos.set(target, {
	// 		duration: 2000,
	// 		easing: cubicOut
	// 	});
	// }

	onMount(() => {
		console.log('App component mounted');
		// Fetch items from API when component mounts
		if(debug) console.log('Calling fetchItems()...');
		fetchItems().then(syncPathFromLocation);
		window.addEventListener('hashchange', handleHashChange);
		window.addEventListener('popstate', handlePopState);
		// Start animation loop
		// animationInterval = setInterval(animateSpaceship, 2000);
		// Start first animation
		// animateSpaceship();
	});

	onDestroy(() => {
		window.removeEventListener('hashchange', handleHashChange);
		window.removeEventListener('popstate', handlePopState);
		// if (animationInterval) {
		// 	clearInterval(animationInterval);
		// }
	});
</script>

<div class="root-grid">
	<GridView items={items} bind:cell bind:panX bind:panY editable={editMode} {selectedKey} {dimParent} focusKey={selectedDetail?.key} on:open={handleDetailOpen} on:openModule={openModule} on:reposition={(e) => updateItemCoords(e.detail.key, e.detail)} on:resize={(e) => updateItemCoords(e.detail.key, e.detail)}>
		<span slot="toolbar-extra" class="status-group">
			<button on:click={fetchItems} class="refresh-btn" title="Refresh items from API">
				Refresh
			</button>
			{#if allowEditMode && !editMode}
				<button on:click={enableEditMode} class="refresh-btn" title="Activer le mode création" style="color:red;border-color:red;">
					Édition
				</button>
			{/if}
			{#if loading}
				<span class="status loading">Loading items...</span>
			{/if}
			{#if error}
				<span class="status error" title={error}>Error loading items</span>
			{/if}
			{#if !loading && !error && (modules.length + details.length) > 0}
				<span class="status success">{modules.length + details.length} items loaded</span>
			{/if}
		</span>
	</GridView>
</div>

{#if path.length > 0 || selectedDetail}
	<ModuleView
		moduleInfo={path.length > 0 ? moduleInfoCache[path[path.length - 1]] : null}
		selectedDetail={selectedDetail}
		on:close={closePanel}
	/>
{/if}

{#if editMode}
<div class="edit-picker">
	<div class="edit-picker-header">
		<span>Mode création</span>
		<button type="button" class="edit-picker-close" on:click={disableEditMode} aria-label="Quitter le mode création">✕</button>
	</div>
	{#if editError}
		<div class="edit-picker-error">{editError}</div>
	{/if}
	<div class="edit-picker-list">
		{#each ['module', 'detail', 'decor'] as t (t)}
			{#each items.filter(i => i.type === t) as it (it.key)}
				<button
					type="button"
					class="edit-picker-item"
					class:active={selectedKey === it.key}
					on:click={() => selectItem(it)}
				>
					<span class="edit-picker-type">{t}</span>
					{it.label || it.key}
				</button>
			{/each}
		{/each}
	</div>
</div>
{/if}

<footer class="social-bar">
	{#each SOCIAL_LINKS as link (link.href)}
	<a href={link.href} target="_blank" rel="noopener" aria-label={link.ariaLabel}>
		<img src="static/img/{link.icon}" alt={link.imgAlt} width="22" height="22" />
		{link.label}
	</a>
	{/each}
</footer>

<button class="social-toggle" on:click={() => socialOpen = true} aria-label="Nos réseaux sociaux">
	<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="20" height="20">
		<circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/>
		<line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
	</svg>
	<span>Nous suivre</span>
</button>

{#if socialOpen}
<div class="social-backdrop" on:click={() => socialOpen = false} aria-hidden="true"></div>
<div class="social-modal" role="dialog" aria-modal="true" aria-label="Réseaux sociaux">
	<div class="social-modal-header">
		<span class="social-modal-title">Nous suivre</span>
		<button class="social-close" on:click={() => socialOpen = false} aria-label="Fermer">✕</button>
	</div>
	<div class="social-modal-grid">
		{#each SOCIAL_LINKS as link (link.href)}
		<a href={link.href} target="_blank" rel="noopener"><img src="static/img/{link.icon}" alt="" width="22" height="22" />{link.label}</a>
		{/each}
	</div>
</div>
{/if}
<style>
	:global(:root) {
		--cell: 80px; /* fallback (will be updated on mount) */
		--cell-h: calc(var(--cell) * 0.866); /* √3/2 */
		--line: rgba(0, 229, 255, 0.3);
		--bg: #0c0a15;
		--cyan: #00e5ff;
		--amber: #ffb347;
	}

	:global(html, body) {
		height: 100%;
	}

	:global(body) {
		margin: 0;
		background: var(--bg);
		font-family: system-ui, -apple-system, Segoe UI, Roboto, Ubuntu, Cantarell, Noto Sans, Helvetica, Arial, "Apple Color Emoji", "Segoe UI Emoji";
	}

	/* Root grid wrapper — GridView positions itself absolutely within this box */
	.root-grid {
		position: fixed;
		inset: 0;
	}

	/* Creative-mode item picker */
	.edit-picker {
		position: fixed;
		top: calc(var(--nav-h, 56px) + 12px);
		right: 12px;
		z-index: 110;
		width: 220px;
		max-height: 60vh;
		display: flex;
		flex-direction: column;
		background: rgba(12, 10, 21, 0.88);
		border: 1px solid rgba(0, 229, 255, 0.2);
		border-radius: 10px;
		backdrop-filter: blur(10px);
		box-shadow: 0 4px 20px rgba(0,0,0,0.6), 0 2px 6px rgba(80,40,160,0.2);
	}
	.edit-picker-header {
		display: flex;
		align-items: center;
		justify-content: space-between;
		padding: 8px 12px;
		font-size: 12px;
		font-weight: 700;
		letter-spacing: 0.06em;
		text-transform: uppercase;
		color: var(--cyan);
		border-bottom: 1px solid rgba(0, 229, 255, 0.15);
	}
	.edit-picker-close {
		background: none;
		border: none;
		color: var(--text-dim, #7a8fa8);
		font-size: 13px;
		text-transform: none;
		letter-spacing: normal;
		cursor: pointer;
		padding: 2px 6px;
		border-radius: 4px;
		transition: color 0.2s;
	}
	.edit-picker-close:hover {
		color: var(--cyan);
	}
	.edit-picker-error {
		padding: 6px 12px;
		font-size: 12px;
		color: #ff6b6b;
		background: rgba(255,77,79,0.1);
	}
	.edit-picker-list {
		overflow-y: auto;
		padding: 6px;
		display: flex;
		flex-direction: column;
		gap: 4px;
	}
	.edit-picker-item {
		display: flex;
		align-items: center;
		gap: 8px;
		padding: 6px 8px;
		background: transparent;
		border: 1px solid transparent;
		border-radius: 6px;
		color: #dde8f0;
		font-size: 12px;
		text-align: left;
		cursor: pointer;
	}
	.edit-picker-item:hover {
		border-color: rgba(0, 229, 255, 0.3);
		background: rgba(0, 229, 255, 0.08);
	}
	.edit-picker-item.active {
		border-color: var(--cyan);
		background: rgba(0, 229, 255, 0.15);
	}
	.edit-picker-type {
		font-size: 10px;
		text-transform: uppercase;
		letter-spacing: 0.04em;
		color: rgba(221, 232, 240, 0.5);
		flex-shrink: 0;
	}

	.refresh-btn {
		padding: 4px 12px;
		background: rgba(0, 229, 255, 0.1);
		border: 1px solid rgba(0, 229, 255, 0.4);
		color: var(--cyan);
		border-radius: 6px;
		cursor: pointer;
		font-size: 12px;
		transition: background 0.2s, box-shadow 0.2s;
	}
	.refresh-btn:hover {
		background: rgba(0, 229, 255, 0.18);
		box-shadow: 0 0 10px rgba(0, 229, 255, 0.2);
	}

	.status-group {
		display: flex;
		gap: 10px;
		align-items: center;
	}

	.status {
		font-size: 12px;
		padding: 4px 8px;
		border-radius: 5px;
		white-space: nowrap;
	}
	.status.loading { color: var(--cyan); background: rgba(0, 229, 255, 0.08); }
	.status.error   { color: #ff6b6b;    background: rgba(255,77,79,0.1); cursor: help; }
	.status.success { color: #6dff8a;    background: rgba(109,255,138,0.08); }

	/* Social bar */
	.social-bar {
		position: fixed;
		bottom: 20px;
		left: 50%;
		transform: translateX(-50%);
		z-index: 10;
		background: rgba(12, 10, 21, 0.85);
		backdrop-filter: blur(12px);
		border: 1px solid rgba(0, 229, 255, 0.14);
		border-radius: 16px;
		padding: 12px 28px;
		display: flex;
		gap: 26px;
		align-items: center;
		box-shadow:
			0 8px 40px rgba(0,0,0,0.7),
			0 2px 8px rgba(80,40,160,0.25),
			0 0 0 1px rgba(0,229,255,0.05);
		white-space: nowrap;
	}

	.social-bar a {
		color: rgba(221, 232, 240, 0.55);
		text-decoration: none;
		display: flex;
		align-items: center;
		gap: 7px;
		font-size: 13px;
		font-weight: 500;
		transition: color 0.2s;
	}

	.social-bar a img {
		opacity: 0.55;
		filter: invert(1);
		transition: opacity 0.2s, filter 0.2s;
	}

	.social-bar a:hover { color: var(--cyan); }
	.social-bar a:hover img {
		opacity: 1;
		filter: invert(1) sepia(1) saturate(5) hue-rotate(145deg);
	}

	/* Toggle button — mobile only */
	.social-toggle {
		display: none;
		position: fixed;
		bottom: 20px;
		right: 20px;
		z-index: 10;
		align-items: center;
		gap: 8px;
		padding: 10px 16px;
		background: rgba(12, 10, 21, 0.88);
		border: 1px solid rgba(0, 229, 255, 0.25);
		border-radius: 50px;
		color: rgba(221, 232, 240, 0.8);
		font-size: 13px;
		font-weight: 500;
		cursor: pointer;
		backdrop-filter: blur(10px);
		box-shadow: 0 4px 20px rgba(0,0,0,0.6), 0 2px 6px rgba(80,40,160,0.2);
		transition: border-color 0.2s, color 0.2s;
	}
	.social-toggle:hover { border-color: var(--cyan); color: var(--cyan); }

	/* Backdrop */
	.social-backdrop {
		position: fixed;
		inset: 0;
		z-index: 40;
		background: rgba(0, 0, 0, 0.55);
		backdrop-filter: blur(3px);
	}

	/* Modal */
	.social-modal {
		position: fixed;
		bottom: 0;
		left: 0; right: 0;
		z-index: 50;
		background: rgba(12, 10, 21, 0.97);
		border-top: 1px solid rgba(0, 229, 255, 0.2);
		border-radius: 18px 18px 0 0;
		padding: 20px 24px 32px;
		box-shadow: 0 -8px 40px rgba(0,0,0,0.7), 0 -2px 8px rgba(80,40,160,0.25);
	}

	.social-modal-header {
		display: flex;
		justify-content: space-between;
		align-items: center;
		margin-bottom: 20px;
	}

	.social-modal-title {
		font-size: 14px;
		font-weight: 600;
		color: var(--cyan);
		letter-spacing: 0.06em;
		text-transform: uppercase;
		text-shadow: 0 0 10px rgba(0, 229, 255, 0.4);
	}

	.social-close {
		background: none;
		border: none;
		color: var(--text-dim, #7a8fa8);
		font-size: 16px;
		cursor: pointer;
		padding: 4px 8px;
		border-radius: 4px;
		transition: color 0.2s;
	}
	.social-close:hover { color: var(--cyan); }

	.social-modal-grid {
		display: grid;
		grid-template-columns: 1fr 1fr;
		gap: 10px;
	}

	.social-modal-grid a {
		display: flex;
		align-items: center;
		gap: 10px;
		padding: 10px 14px;
		border: 1px solid rgba(0, 229, 255, 0.12);
		border-radius: 8px;
		color: rgba(221, 232, 240, 0.7);
		text-decoration: none;
		font-size: 13px;
		background: rgba(90, 60, 180, 0.06);
		transition: border-color 0.2s, color 0.2s;
	}

	.social-modal-grid a img {
		opacity: 0.6;
		filter: invert(1);
		flex-shrink: 0;
	}

	.social-modal-grid a:hover {
		color: var(--cyan);
		border-color: rgba(0, 229, 255, 0.35);
	}
	.social-modal-grid a:hover img {
		opacity: 1;
		filter: invert(1) sepia(1) saturate(5) hue-rotate(145deg);
	}

	@media (max-width: 700px) {
		:global(:root) { --cell: 48px; }
		.social-bar { display: none; }
		.social-toggle { display: flex; }
	}
</style>


